import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { ClaudeClient, VISION_EXTRACTION_PROMPT, REQUIREMENTS_EXTRACTION_SYSTEM_PROMPT } from '../api/claude-client.js';
import { EmbeddingClient } from '../api/embedding-client.js';
import type { EmbeddingProvider } from '../api/embedding-client.js';
import { VectorClient } from '../api/vector-client.js';
import type {
  ExtractedRequirement,
  ExtractionResult,
  IngestionPhase,
  UploadSource,
  IngestionApiKeys,
  VectorDbConfig,
  GenerateSysmlOptions,
  DuplicateMatch,
  IngestionHistoryEntry,
  IngestionJournal,
  JournalStep,
  JournalMetrics,
  ClaudeApiUsage,
  EditDelta,
} from '../api/ingestion-types.js';
import { getSchemaById } from '@arctek/sysml-builder';
import { parseSysML } from '@arctek/sysml-core';
import { STORAGE_KEYS } from '../utils/constants.js';

// ---------------------------------------------------------------------------
// Model context — provided by the host so extraction + generation are
// aware of what already exists in the user's SysML model.
// ---------------------------------------------------------------------------

/** Summary of an existing model element for context passing */
export interface ModelElementSummary {
  name: string;
  type: string; // 'PartDefinition', 'RequirementDefinition', etc.
  id?: string;  // SysML element id (e.g., 'SAF-001')
}

/** Full model context passed to Claude for model-aware extraction/generation */
export interface IngestionModelContext {
  /** Current SysML v2 source text in the editor */
  sourceText: string;
  /** Existing element names and types from the parsed model */
  elements: ModelElementSummary[];
  /** Existing requirement IDs/names (for duplicate/traceability awareness) */
  requirementNames: string[];
  /** Package name(s) in the current model */
  packageNames: string[];
}

/** Callback that the host provides to supply current model state */
export type ModelContextProvider = () => IngestionModelContext | null;

// ---------------------------------------------------------------------------
// IngestionController — AI-powered requirements extraction from documents
//
// Pattern: Lit ReactiveController (follows ChatbotController exactly)
//
// State machine: idle → uploading → extracting → reviewing →
//                checking-duplicates → committing → done
//
// Features:
//   - File upload (image, PDF, text, CSV)
//   - Claude vision for image→text extraction
//   - Structured requirement extraction via Claude
//   - Manual review/edit of extracted requirements
//   - Duplicate detection via vector similarity search
//   - SysML v2 code generation (local builder schema + AI fallback)
//   - Parse validation of generated code
//   - Commit to editor via insert-code event
//   - API key + vector DB configuration with localStorage persistence
// ---------------------------------------------------------------------------

/** Maximum file sizes */
const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20MB
const MAX_TEXT_SIZE = 5 * 1024 * 1024; // 5MB

/** Accepted MIME types */
const IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);
const TEXT_TYPES = new Set([
  'text/plain',
  'text/csv',
  'text/markdown',
  'application/json',
]);

type ImageMediaType = 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp';

export class IngestionController implements ReactiveController {
  private host: ReactiveControllerHost & HTMLElement;

  // ── Clients ────────────────────────────────────────────────────────────
  private claude = new ClaudeClient();
  private embedding = new EmbeddingClient();
  private vector = new VectorClient(this.embedding);

  /** Provider callback for current model context — set by the host */
  modelContextProvider: ModelContextProvider = () => null;

  // ── Core state ─────────────────────────────────────────────────────────

  generation = 0;

  /** Current pipeline phase */
  phase: IngestionPhase = 'idle';

  /** The uploaded source file */
  uploadSource: UploadSource | null = null;

  /** Raw text extracted from the document */
  rawText = '';

  /** Extracted requirements */
  requirements: ExtractedRequirement[] = [];

  /** Extraction warnings */
  warnings: string[] = [];

  /** Generated SysML code (committing phase) */
  generatedCode = '';

  /** Parse validation diagnostics for generated code */
  parseDiagnostics: Array<{ severity: string; message: string; line?: number }> = [];

  /** Whether the generated code parses successfully */
  parseSuccess = false;

  /** Generation options */
  generateOptions: GenerateSysmlOptions = {
    insertionPoint: 'inside-package',
    wrapInPackage: false,
    includeRationale: true,
    includeSource: true,
  };

  /** Ingestion history */
  history: IngestionHistoryEntry[] = [];

  /** Settings overlay visibility */
  showSettings = false;

  // ── Journal state ──────────────────────────────────────────────────────

  /** Active journal for the current ingestion session */
  activeJournal: IngestionJournal | null = null;

  /** Completed journals (persisted to localStorage) */
  journals: IngestionJournal[] = [];

  /** Snapshot of requirements at review start — used to compute user-review diffs */
  private _reviewStartSnapshot = new Map<string, ExtractedRequirement>();

  /** Track removed requirement names during review */
  private _removedDuringReview = new Set<string>();

  /** Track the count before review for detecting removals */
  private _reviewStartCount = 0;

  /** ISO timestamp when the user entered review phase */
  private _reviewStartedAt: string | null = null;

  // ── Loading / error state ──────────────────────────────────────────────

  isLoading = false;
  error: string | null = null;
  progressMessage = '';

  // ── Computed getters ───────────────────────────────────────────────────

  get isConfigured(): boolean {
    return this.claude.isConfigured;
  }

  get isVectorConfigured(): boolean {
    return this.vector.isConfigured;
  }

  get acceptedRequirements(): ExtractedRequirement[] {
    return this.requirements.filter((r) => r.accepted);
  }

  get totalConfidence(): number {
    if (this.requirements.length === 0) return 0;
    const sum = this.requirements.reduce((acc, r) => acc + r.confidence, 0);
    return Math.round((sum / this.requirements.length) * 100);
  }

  get hasDuplicates(): boolean {
    return this.requirements.some(
      (r) => r.duplicates && r.duplicates.length > 0,
    );
  }

  // ── Constructor / lifecycle ────────────────────────────────────────────

  constructor(host: ReactiveControllerHost & HTMLElement) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    this._loadApiKeys();
    this._loadHistory();
    this._loadJournals();
  }

  hostDisconnected(): void {
    // No cleanup needed
  }

  // ════════════════════════════════════════════════════════════════════════
  // API Key Management
  // ════════════════════════════════════════════════════════════════════════

  saveApiKeys(keys: IngestionApiKeys): void {
    this.claude.configure(keys.anthropicKey);

    if (keys.embeddingKey && keys.embeddingProvider) {
      this.embedding.configure(keys.embeddingKey, keys.embeddingProvider);
    }

    try {
      localStorage.setItem(STORAGE_KEYS.INGESTION_API_KEYS, JSON.stringify(keys));
    } catch {
      // Storage full — ignore
    }

    this._update();
  }

  saveVectorConfig(config: VectorDbConfig, apiKey: string): void {
    this.vector.configure(config, apiKey);

    try {
      localStorage.setItem(
        'sysml:vector-config',
        JSON.stringify({ config, apiKey }),
      );
    } catch {
      // Storage full — ignore
    }

    this._update();
  }

  async testVectorConnection(): Promise<{ ok: boolean; message: string }> {
    return this.vector.testConnection();
  }

  // ════════════════════════════════════════════════════════════════════════
  // File Upload
  // ════════════════════════════════════════════════════════════════════════

  async handleFileUpload(file: File): Promise<void> {
    this.error = null;

    // Validate file size
    if (IMAGE_TYPES.has(file.type) && file.size > MAX_IMAGE_SIZE) {
      this.error = `Image too large (${Math.round(file.size / 1024 / 1024)}MB). Maximum is 20MB.`;
      this._update();
      return;
    }
    if (TEXT_TYPES.has(file.type) && file.size > MAX_TEXT_SIZE) {
      this.error = `Text file too large (${Math.round(file.size / 1024 / 1024)}MB). Maximum is 5MB.`;
      this._update();
      return;
    }

    // Validate file type
    if (!IMAGE_TYPES.has(file.type) && !TEXT_TYPES.has(file.type) && file.type !== 'application/pdf') {
      this.error = `Unsupported file type: ${file.type}. Accepted: images (JPEG, PNG, GIF, WebP), text, CSV, PDF.`;
      this._update();
      return;
    }

    this.phase = 'uploading';
    this.progressMessage = `Reading ${file.name}...`;
    this._update();

    try {
      const uploadStart = Date.now();
      const data = await this._readFile(file);

      const sourceType = IMAGE_TYPES.has(file.type)
        ? 'image' as const
        : file.type === 'application/pdf'
          ? 'pdf' as const
          : file.type === 'text/csv'
            ? 'csv' as const
            : 'text' as const;

      this.uploadSource = {
        id: crypto.randomUUID(),
        filename: file.name,
        type: sourceType,
        mimeType: file.type,
        sizeBytes: file.size,
        data,
        uploadedAt: new Date().toISOString(),
      };

      // If it's already text, skip vision step
      if (sourceType === 'text' || sourceType === 'csv') {
        this.rawText = data;
      }

      // Generate thumbnail + hash for audit trail (images only)
      let thumbnail: string | undefined;
      let imageDimensions: { w: number; h: number } | undefined;
      let dataHash: string | undefined;
      if (sourceType === 'image') {
        try {
          const thumbResult = await this._generateThumbnail(data, file.type);
          thumbnail = thumbResult.thumbnail;
          imageDimensions = { w: thumbResult.width, h: thumbResult.height };
        } catch { /* non-fatal */ }
      }
      try {
        dataHash = await this._hashData(data.slice(0, 10000));
      } catch { /* non-fatal */ }

      // Journal: start + document-uploaded step
      this._startJournal(file.name);
      this._addJournalStep({
        id: crypto.randomUUID(),
        kind: 'document-uploaded',
        timestamp: new Date().toISOString(),
        durationMs: Date.now() - uploadStart,
        filename: file.name,
        sourceType,
        mimeType: file.type,
        sizeBytes: file.size,
        thumbnail,
        dataHash,
        imageDimensions,
      });

      // Auto-proceed to extraction
      await this.extractRequirements();
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'File upload failed';
      this._addJournalStep({
        id: crypto.randomUUID(),
        kind: 'error',
        timestamp: new Date().toISOString(),
        durationMs: 0,
        failedPhase: 'uploading',
        message: this.error,
      });
      this.phase = 'idle';
      this._update();
    }
  }

  /** Handle pasted text input */
  async handlePastedText(text: string): Promise<void> {
    if (!text.trim()) return;

    this.error = null;
    this.phase = 'uploading';
    this.progressMessage = 'Processing pasted text...';
    this._update();

    const sizeBytes = new Blob([text]).size;

    this.uploadSource = {
      id: crypto.randomUUID(),
      filename: 'pasted-text',
      type: 'text',
      mimeType: 'text/plain',
      sizeBytes,
      data: text,
      uploadedAt: new Date().toISOString(),
    };

    this.rawText = text;

    // Journal: start + document-uploaded step
    this._startJournal('pasted-text');
    this._addJournalStep({
      id: crypto.randomUUID(),
      kind: 'document-uploaded',
      timestamp: new Date().toISOString(),
      durationMs: 0,
      filename: 'pasted-text',
      sourceType: 'text',
      mimeType: 'text/plain',
      sizeBytes,
    });

    await this.extractRequirements();
  }

  // ════════════════════════════════════════════════════════════════════════
  // Requirement Extraction
  // ════════════════════════════════════════════════════════════════════════

  async extractRequirements(): Promise<void> {
    if (!this.claude.isConfigured) {
      this.error = 'Claude API key not configured. Open settings to add your key.';
      this.showSettings = true;
      this.phase = 'idle';
      this._update();
      return;
    }

    this.phase = 'extracting';
    this.isLoading = true;
    this.error = null;
    this._update();

    try {
      // Step 1: If image source, extract text via vision first
      if (
        this.uploadSource?.type === 'image' &&
        !this.rawText
      ) {
        this.progressMessage = 'Extracting text from image...';
        this._update();

        const visionStart = Date.now();
        const visionResult = await this.claude.extractTextFromImage(
          this.uploadSource.data,
          this.uploadSource.mimeType as ImageMediaType,
        );
        this.rawText = visionResult.data;

        // Journal: text-extracted (vision)
        this._addJournalStep({
          id: crypto.randomUUID(),
          kind: 'text-extracted',
          timestamp: new Date().toISOString(),
          durationMs: Date.now() - visionStart,
          method: 'vision',
          textLength: this.rawText.length,
          textPreview: this.rawText.slice(0, 500),
          apiUsage: visionResult.usage,
          fullText: this.rawText.slice(0, 10000),
          promptSent: VISION_EXTRACTION_PROMPT,
        });
      } else if (this.rawText) {
        // Journal: text-extracted (passthrough — already had text)
        this._addJournalStep({
          id: crypto.randomUUID(),
          kind: 'text-extracted',
          timestamp: new Date().toISOString(),
          durationMs: 0,
          method: 'passthrough',
          textLength: this.rawText.length,
          textPreview: this.rawText.slice(0, 500),
          fullText: this.rawText.slice(0, 10000),
        });
      }

      if (!this.rawText.trim()) {
        this.error = 'No text could be extracted from the document.';
        this.phase = 'idle';
        this.isLoading = false;
        this._addJournalStep({
          id: crypto.randomUUID(),
          kind: 'error',
          timestamp: new Date().toISOString(),
          durationMs: 0,
          failedPhase: 'extracting',
          message: this.error,
        });
        this._update();
        return;
      }

      // Step 2: Extract structured requirements from text
      this.progressMessage = 'Analyzing requirements...';
      this._update();

      // Build model context string for Claude
      const modelCtx = this.modelContextProvider();
      const contextStr = modelCtx ? this._buildExtractionContext(modelCtx) : undefined;

      const result: ExtractionResult = await this.claude.extractRequirements(
        this.rawText,
        contextStr,
      );

      this.requirements = result.requirements;
      this.warnings = result.warnings;

      // Compute confidence distribution
      const confDist = { high: 0, med: 0, low: 0 };
      for (const r of result.requirements) {
        if (r.confidence >= 0.8) confDist.high++;
        else if (r.confidence >= 0.5) confDist.med++;
        else confDist.low++;
      }

      // Journal: requirements-extracted step
      this._addJournalStep({
        id: crypto.randomUUID(),
        kind: 'requirements-extracted',
        timestamp: new Date().toISOString(),
        durationMs: result.durationMs,
        count: result.requirements.length,
        summaries: result.requirements.map((r) => ({ name: r.name, title: r.title })),
        avgConfidence: result.requirements.length > 0
          ? result.requirements.reduce((sum, r) => sum + r.confidence, 0) / result.requirements.length
          : 0,
        warnings: result.warnings,
        apiUsage: result.apiUsage,
        hadModelContext: !!contextStr,
        fullRequirements: result.requirements,
        systemPromptSent: result.systemPromptSent,
        contextStringSent: result.contextStringSent,
        confidenceDistribution: confDist,
        rawResponseText: result.rawResponseText,
      });

      if (this.requirements.length === 0) {
        this.error = 'No requirements found in the document. Try a different file or paste the text directly.';
        this.phase = 'idle';
      } else {
        this.phase = 'reviewing';
        this._reviewStartedAt = new Date().toISOString();
        // Snapshot requirements for user-review diff tracking
        this._reviewStartSnapshot.clear();
        this._removedDuringReview.clear();
        this._reviewStartCount = this.requirements.length;
        for (const r of this.requirements) {
          this._reviewStartSnapshot.set(r.id, { ...r });
        }
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Extraction failed';
      this._addJournalStep({
        id: crypto.randomUUID(),
        kind: 'error',
        timestamp: new Date().toISOString(),
        durationMs: 0,
        failedPhase: 'extracting',
        message: this.error,
      });
      this.phase = 'idle';
    } finally {
      this.isLoading = false;
      this.progressMessage = '';
      this._update();
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // Review Phase — Edit / Remove / Accept
  // ════════════════════════════════════════════════════════════════════════

  updateRequirement(id: string, updates: Partial<ExtractedRequirement>): void {
    this.requirements = this.requirements.map((r) =>
      r.id === id ? { ...r, ...updates } : r,
    );
    this._update();
  }

  removeRequirement(id: string): void {
    const removed = this.requirements.find((r) => r.id === id);
    if (removed) {
      this._removedDuringReview.add(removed.name);
    }
    this.requirements = this.requirements.filter((r) => r.id !== id);
    this._update();
  }

  toggleRequirement(id: string): void {
    this.requirements = this.requirements.map((r) =>
      r.id === id ? { ...r, accepted: !r.accepted } : r,
    );
    this._update();
  }

  acceptAll(): void {
    this.requirements = this.requirements.map((r) => ({ ...r, accepted: true }));
    this._update();
  }

  rejectAll(): void {
    this.requirements = this.requirements.map((r) => ({ ...r, accepted: false }));
    this._update();
  }

  // ════════════════════════════════════════════════════════════════════════
  // Duplicate Detection
  // ════════════════════════════════════════════════════════════════════════

  async checkDuplicates(): Promise<void> {
    if (!this.vector.isConfigured) {
      // Skip silently — vector DB is optional
      this.warnings = [
        ...this.warnings,
        'Vector DB not configured — duplicate detection skipped.',
      ];
      this._update();
      return;
    }

    this.phase = 'checking-duplicates';
    this.isLoading = true;
    this.progressMessage = 'Checking for duplicates...';
    this._update();

    const dupStart = Date.now();
    const dupResults: Array<{ name: string; duplicateCount: number; topSimilarity: number }> = [];
    let totalDupsFound = 0;

    try {
      for (const req of this.acceptedRequirements) {
        const results = await this.vector.findSimilar(
          `${req.name}: ${req.text}`,
          5,
          0.85,
        );

        const duplicates: DuplicateMatch[] = results.map((r) => ({
          vectorId: r.id,
          name: r.metadata.requirementName,
          text: r.metadata.text,
          similarity: r.score,
          sourceDocument: r.metadata.source,
          ingestedAt: r.metadata.ingestedAt,
        }));

        this.updateRequirement(req.id, { duplicates });

        dupResults.push({
          name: req.name,
          duplicateCount: duplicates.length,
          topSimilarity: duplicates.length > 0 ? Math.max(...duplicates.map((d) => d.similarity)) : 0,
        });
        totalDupsFound += duplicates.length;
      }

      // Journal: duplicate-check step
      this._addJournalStep({
        id: crypto.randomUUID(),
        kind: 'duplicate-check',
        timestamp: new Date().toISOString(),
        durationMs: Date.now() - dupStart,
        checkedCount: this.acceptedRequirements.length,
        duplicatesFoundCount: totalDupsFound,
        results: dupResults,
      });

      this.phase = 'reviewing';
    } catch (err) {
      // Non-fatal — log and continue
      const msg = err instanceof Error ? err.message : String(err);
      this.warnings = [...this.warnings, `Duplicate check failed: ${msg}`];
      this.phase = 'reviewing';
    } finally {
      this.isLoading = false;
      this.progressMessage = '';
      this._update();
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // SysML Generation
  // ════════════════════════════════════════════════════════════════════════

  async generateSysml(options?: Partial<GenerateSysmlOptions>): Promise<void> {
    if (options) {
      this.generateOptions = { ...this.generateOptions, ...options };
    }

    this.phase = 'committing';
    this.isLoading = true;
    this.error = null;
    this.progressMessage = 'Generating SysML...';
    this._update();

    try {
      // Journal: user-review step (diff snapshot vs current state)
      this._recordUserReviewStep();

      const accepted = this.acceptedRequirements;
      if (accepted.length === 0) {
        this.error = 'No requirements accepted. Accept at least one requirement.';
        this.phase = 'reviewing';
        this.isLoading = false;
        this._update();
        return;
      }

      // Try local generation first (using builder schema)
      const genStart = Date.now();
      let code = this._generateLocal(accepted);
      let genMethod: 'local' | 'ai' = 'local';
      let genUsage: ClaudeApiUsage | undefined;

      // Validate the generated code
      let validation = this._validateCode(code);

      // If local generation produces invalid code, fall back to AI
      let fallbackReason: string | undefined;
      let promptSent: string | undefined;
      if (!validation.success && this.claude.isConfigured) {
        this.progressMessage = 'Refining with AI...';
        this._update();
        fallbackReason = `Local generation produced ${validation.diagnostics.length} parse errors`;
        genMethod = 'ai';
        const modelCtx = this.modelContextProvider();
        const aiResult = await this.claude.generateSysmlFromRequirements(
          accepted,
          modelCtx?.sourceText,
        );
        code = aiResult.data;
        genUsage = aiResult.usage;
        promptSent = aiResult.userPrompt;
        validation = this._validateCode(code);
      }

      this.generatedCode = code;
      this.parseSuccess = validation.success;
      this.parseDiagnostics = validation.diagnostics;

      // Journal: code-generated step
      this._addJournalStep({
        id: crypto.randomUUID(),
        kind: 'code-generated',
        timestamp: new Date().toISOString(),
        durationMs: Date.now() - genStart,
        method: genMethod,
        codeLength: code.length,
        codePreview: code.slice(0, 500),
        parseSuccess: validation.success,
        diagnosticCount: validation.diagnostics.length,
        apiUsage: genUsage,
        fullCode: code,
        fullDiagnostics: validation.diagnostics,
        generationOptions: { ...this.generateOptions },
        promptSent,
        fallbackReason,
      });
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Generation failed';
      this._addJournalStep({
        id: crypto.randomUUID(),
        kind: 'error',
        timestamp: new Date().toISOString(),
        durationMs: 0,
        failedPhase: 'committing',
        message: this.error,
      });
    } finally {
      this.isLoading = false;
      this.progressMessage = '';
      this._update();
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // Commit — Finalize and emit insert-code event
  // ════════════════════════════════════════════════════════════════════════

  commit(): { code: string; insertionPoint: string } | null {
    if (!this.generatedCode) return null;

    const result = {
      code: this.generatedCode,
      insertionPoint: this.generateOptions.insertionPoint,
    };

    // Journal: user-committed step
    this._addJournalStep({
      id: crypto.randomUUID(),
      kind: 'user-committed',
      timestamp: new Date().toISOString(),
      durationMs: 0,
      codePreview: this.generatedCode.slice(0, 500),
      insertionPoint: this.generateOptions.insertionPoint,
      requirementCount: this.acceptedRequirements.length,
      fullCode: this.generatedCode,
    });
    this._completeJournal('committed');

    // Store in vector DB (fire-and-forget)
    this._storeInVectorDb();

    // Add to history
    this._addHistoryEntry(true);

    // Reset for next ingestion
    this.phase = 'done';
    this._update();

    return result;
  }

  /** Reset the controller to idle state for a new ingestion */
  reset(): void {
    // Complete active journal as cancelled (if one is in progress)
    if (this.activeJournal) {
      this._completeJournal('cancelled');
    }

    this.phase = 'idle';
    this.uploadSource = null;
    this.rawText = '';
    this.requirements = [];
    this.warnings = [];
    this.generatedCode = '';
    this.parseDiagnostics = [];
    this.parseSuccess = false;
    this.error = null;
    this.progressMessage = '';
    this.isLoading = false;
    this._reviewStartSnapshot.clear();
    this._removedDuringReview.clear();
    this._reviewStartCount = 0;
    this._reviewStartedAt = null;
    this._update();
  }

  // ════════════════════════════════════════════════════════════════════════
  // Internal: Build extraction context from existing model
  // ════════════════════════════════════════════════════════════════════════

  private _buildExtractionContext(ctx: IngestionModelContext): string {
    const lines: string[] = [];

    lines.push('This document is being ingested into an existing SysML v2 model.');

    if (ctx.packageNames.length > 0) {
      lines.push(`\nExisting packages: ${ctx.packageNames.join(', ')}`);
    }

    if (ctx.requirementNames.length > 0) {
      lines.push(`\nExisting requirement definitions already in the model (do NOT duplicate these):`);
      lines.push(ctx.requirementNames.map((n) => `  - ${n}`).join('\n'));
    }

    const nonReqElements = ctx.elements.filter(
      (e) => e.type !== 'RequirementDefinition',
    );
    if (nonReqElements.length > 0) {
      lines.push(`\nOther existing model elements (available for traceability):`);
      for (const el of nonReqElements) {
        lines.push(`  - ${el.name} (${el.type})`);
      }
    }

    lines.push('\nWhen extracting requirements from this document:');
    lines.push('- Flag any that appear to already exist in the model (by name or semantics)');
    lines.push('- Suggest traceability: if a requirement references or derives from an existing element, include it as parentId');
    lines.push('- Use naming conventions consistent with the existing model');

    return lines.join('\n');
  }

  // ════════════════════════════════════════════════════════════════════════
  // Internal: Local SysML generation using builder schema
  // ════════════════════════════════════════════════════════════════════════

  private _generateLocal(requirements: ExtractedRequirement[]): string {
    const schema = getSchemaById('requirement-def');
    if (!schema) {
      // Fallback: manual generation
      return this._generateManual(requirements);
    }

    const lines: string[] = [];

    if (this.generateOptions.wrapInPackage) {
      const pkgName = this.generateOptions.packageName || 'IngestedRequirements';
      lines.push(`package ${pkgName} {`);
      lines.push('');
    }

    // Group by parent for hierarchy
    const topLevel = requirements.filter((r) => !r.parentId || r.depth === 0);
    const byParent = new Map<string, ExtractedRequirement[]>();
    for (const r of requirements) {
      if (r.parentId) {
        const children = byParent.get(r.parentId) ?? [];
        children.push(r);
        byParent.set(r.parentId, children);
      }
    }

    const indent = this.generateOptions.wrapInPackage ? '  ' : '';

    for (const req of topLevel) {
      const code = schema.generate({
        name: req.name,
        title: req.title,
        text: req.text,
        priority: req.priority,
        rationale: this.generateOptions.includeRationale ? (req.rationale ?? '') : '',
        source: this.generateOptions.includeSource ? (req.source ?? '') : '',
      });

      // Indent if inside a package
      const indented = indent
        ? code.split('\n').map((l) => indent + l).join('\n')
        : code;

      lines.push(indented);

      // Add child requirements as nested usages
      const children = byParent.get(req.name);
      if (children?.length) {
        // Insert children before the closing brace
        const lastBrace = lines.length - 1;
        const closingLine = lines[lastBrace];
        lines[lastBrace] = ''; // Remove closing brace temporarily

        for (const child of children) {
          lines.push(`${indent}  requirement ${child.name} : ${req.name} {`);
          lines.push(`${indent}    doc /* ${child.title} */`);
          if (child.text) {
            lines.push(`${indent}    /* ${child.text} */`);
          }
          lines.push(`${indent}  }`);
        }

        lines.push(closingLine); // Restore closing brace
      }

      lines.push('');
    }

    if (this.generateOptions.wrapInPackage) {
      lines.push('}');
    }

    return lines.join('\n').trim();
  }

  /** Fallback manual generation when builder schema isn't available */
  private _generateManual(requirements: ExtractedRequirement[]): string {
    const lines: string[] = [];

    for (const req of requirements) {
      lines.push(`requirement def ${req.name} {`);
      lines.push(`  doc /* ${req.title} */`);
      lines.push('');
      lines.push(`  /* ${req.text} */`);
      if (req.priority || req.source) {
        const meta: string[] = [];
        if (req.priority) meta.push(`priority: "${req.priority}"`);
        if (req.source && this.generateOptions.includeSource) {
          meta.push(`source: "${req.source}"`);
        }
        lines.push(`  @meta { ${meta.join(', ')} }`);
      }
      if (req.rationale && this.generateOptions.includeRationale) {
        lines.push('');
        lines.push(`  /* Rationale: ${req.rationale} */`);
      }
      lines.push('}');
      lines.push('');
    }

    return lines.join('\n').trim();
  }

  // ════════════════════════════════════════════════════════════════════════
  // Internal: Parse validation
  // ════════════════════════════════════════════════════════════════════════

  private _validateCode(code: string): {
    success: boolean;
    diagnostics: Array<{ severity: string; message: string; line?: number }>;
  } {
    try {
      const result = parseSysML(code);
      const diagnostics = (result.diagnostics ?? []).map((d) => ({
        severity: d.severity,
        message: d.message,
        line: d.line,
      }));
      const hasErrors = diagnostics.some((d) => d.severity === 'error');
      return { success: !hasErrors, diagnostics };
    } catch {
      return {
        success: false,
        diagnostics: [{ severity: 'error', message: 'Failed to parse generated code' }],
      };
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // Internal: Vector DB storage (fire-and-forget on commit)
  // ════════════════════════════════════════════════════════════════════════

  private async _storeInVectorDb(): Promise<void> {
    if (!this.vector.isConfigured) return;

    try {
      const docId = this.uploadSource?.id ?? crypto.randomUUID();
      await this.vector.upsertRequirements(
        this.acceptedRequirements.map((r) => ({
          id: r.id,
          name: r.name,
          text: r.text,
          priority: r.priority,
          source: r.source,
          documentId: docId,
        })),
      );
    } catch (err) {
      console.error('[IngestionController] Vector upsert failed:', err);
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // Internal: File reading
  // ════════════════════════════════════════════════════════════════════════

  private async _readFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        if (TEXT_TYPES.has(file.type)) {
          resolve(reader.result as string);
        } else {
          // For images/PDFs: strip data URL prefix to get base64
          const dataUrl = reader.result as string;
          const base64 = dataUrl.split(',')[1];
          resolve(base64);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));

      if (TEXT_TYPES.has(file.type)) {
        reader.readAsText(file);
      } else {
        reader.readAsDataURL(file);
      }
    });
  }

  // ════════════════════════════════════════════════════════════════════════
  // Internal: Persistence
  // ════════════════════════════════════════════════════════════════════════

  private _loadApiKeys(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.INGESTION_API_KEYS);
      if (raw) {
        const keys = JSON.parse(raw) as IngestionApiKeys;
        if (keys.anthropicKey) {
          this.claude.configure(keys.anthropicKey);
        }
        if (keys.embeddingKey && keys.embeddingProvider) {
          this.embedding.configure(keys.embeddingKey, keys.embeddingProvider);
        }
      }

      const vectorRaw = localStorage.getItem('sysml:vector-config');
      if (vectorRaw) {
        const { config, apiKey } = JSON.parse(vectorRaw) as {
          config: VectorDbConfig;
          apiKey: string;
        };
        this.vector.configure(config, apiKey);
      }
    } catch {
      // Ignore parse errors
    }
  }

  private _loadHistory(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.INGESTION_HISTORY);
      if (raw) {
        this.history = JSON.parse(raw) as IngestionHistoryEntry[];
      }
    } catch {
      this.history = [];
    }
  }

  private _addHistoryEntry(committed: boolean): void {
    const entry: IngestionHistoryEntry = {
      id: crypto.randomUUID(),
      filename: this.uploadSource?.filename ?? 'unknown',
      requirementCount: this.acceptedRequirements.length,
      ingestedAt: new Date().toISOString(),
      committed,
    };

    this.history = [entry, ...this.history].slice(0, 50); // Keep last 50

    try {
      localStorage.setItem(
        STORAGE_KEYS.INGESTION_HISTORY,
        JSON.stringify(this.history),
      );
    } catch {
      // Storage full — ignore
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // Internal: Journal management
  // ════════════════════════════════════════════════════════════════════════

  private _startJournal(filename: string): void {
    // Complete any existing active journal before starting a new one
    if (this.activeJournal) {
      this._completeJournal('cancelled');
    }
    this.activeJournal = {
      id: crypto.randomUUID(),
      startedAt: new Date().toISOString(),
      completedAt: null,
      outcome: null,
      filename,
      steps: [],
      metrics: this._emptyMetrics(),
    };
    this._update();
  }

  private _addJournalStep(step: JournalStep): void {
    if (!this.activeJournal) return;
    this.activeJournal.steps.push(step);
    this.activeJournal.metrics = this._recomputeMetrics(this.activeJournal);
    this._update();
  }

  private _completeJournal(outcome: 'committed' | 'cancelled' | 'error'): void {
    if (!this.activeJournal) return;
    this.activeJournal.completedAt = new Date().toISOString();
    this.activeJournal.outcome = outcome;
    this.activeJournal.metrics = this._recomputeMetrics(this.activeJournal);

    // Add to completed list (most recent first)
    this.journals = [this.activeJournal, ...this.journals].slice(0, 25);
    this._saveJournals();
    this.activeJournal = null;
    this._update();
  }

  /** Compute a user-review step by diffing the snapshot vs current state */
  private _recordUserReviewStep(): void {
    if (this._reviewStartSnapshot.size === 0) return;

    const decisions: Array<{
      name: string;
      action: 'accepted' | 'rejected' | 'edited' | 'removed';
      editDeltas?: EditDelta[];
    }> = [];
    let acceptedCount = 0;
    let rejectedCount = 0;
    let editedCount = 0;

    for (const req of this.requirements) {
      const orig = this._reviewStartSnapshot.get(req.id);
      if (!orig) {
        // New — shouldn't happen in normal flow but count as accepted
        if (req.accepted) acceptedCount++;
        else rejectedCount++;
        continue;
      }

      const wasEdited =
        orig.name !== req.name ||
        orig.title !== req.title ||
        orig.text !== req.text ||
        orig.priority !== req.priority;

      if (wasEdited) {
        // Compute field-level deltas
        const editDeltas: EditDelta[] = [];
        if (orig.name !== req.name) editDeltas.push({ field: 'name', before: orig.name, after: req.name });
        if (orig.title !== req.title) editDeltas.push({ field: 'title', before: orig.title, after: req.title });
        if (orig.text !== req.text) editDeltas.push({ field: 'text', before: orig.text, after: req.text });
        if (orig.priority !== req.priority) editDeltas.push({ field: 'priority', before: orig.priority, after: req.priority });
        decisions.push({ name: req.name, action: 'edited', editDeltas });
        editedCount++;
      } else if (!req.accepted) {
        decisions.push({ name: req.name, action: 'rejected' });
        rejectedCount++;
      } else {
        decisions.push({ name: req.name, action: 'accepted' });
        acceptedCount++;
      }
    }

    // Track removed requirements
    const removedCount = this._removedDuringReview.size;
    for (const name of this._removedDuringReview) {
      decisions.push({ name, action: 'removed' });
    }

    const reviewEndedAt = new Date().toISOString();

    this._addJournalStep({
      id: crypto.randomUUID(),
      kind: 'user-review',
      timestamp: new Date().toISOString(),
      durationMs: 0, // user time — not meaningful to measure
      acceptedCount,
      rejectedCount,
      editedCount,
      removedCount,
      decisions,
      reviewStartedAt: this._reviewStartedAt ?? undefined,
      reviewEndedAt,
    });
  }

  private _recomputeMetrics(journal: IngestionJournal): JournalMetrics {
    const metrics = this._emptyMetrics();

    if (journal.steps.length > 0) {
      const firstStep = journal.steps[0];
      const lastStep = journal.steps[journal.steps.length - 1];
      const startMs = new Date(firstStep.timestamp).getTime();
      const endMs = new Date(lastStep.timestamp).getTime() + lastStep.durationMs;
      metrics.totalDurationMs = endMs - startMs;
    }

    for (const step of journal.steps) {
      const usage = 'apiUsage' in step ? (step as { apiUsage?: ClaudeApiUsage }).apiUsage : undefined;
      if (usage) {
        metrics.apiCallCount++;
        metrics.totalInputTokens += usage.inputTokens;
        metrics.totalOutputTokens += usage.outputTokens;
      }

      if (step.kind === 'requirements-extracted') {
        metrics.extractedCount = step.count;
        metrics.avgConfidence = step.avgConfidence;
      }
      if (step.kind === 'user-review') {
        metrics.acceptedCount = step.acceptedCount;
      }
      if (step.kind === 'code-generated') {
        metrics.parseSuccess = step.parseSuccess;
      }
    }

    return metrics;
  }

  private _emptyMetrics(): JournalMetrics {
    return {
      totalDurationMs: 0,
      apiCallCount: 0,
      totalInputTokens: 0,
      totalOutputTokens: 0,
      extractedCount: 0,
      acceptedCount: 0,
      avgConfidence: 0,
      parseSuccess: null,
    };
  }

  private _loadJournals(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.INGESTION_JOURNALS);
      if (raw) {
        this.journals = JSON.parse(raw) as IngestionJournal[];
      }
    } catch {
      this.journals = [];
    }
  }

  private _saveJournals(): void {
    try {
      // Strip large enriched fields before persisting to keep ~3-5KB per journal
      const stripped = this.journals.map((j) => ({
        ...j,
        steps: j.steps.map((s) => {
          if (s.kind === 'document-uploaded') {
            const { thumbnail: _t, ...rest } = s;
            return rest;
          }
          if (s.kind === 'text-extracted') {
            const { fullText: _ft, promptSent: _ps, ...rest } = s;
            return { ...rest, textPreview: rest.textPreview.slice(0, 500) };
          }
          if (s.kind === 'requirements-extracted') {
            const { fullRequirements: _fr, systemPromptSent: _sp, contextStringSent: _cs, rawResponseText: _rr, ...rest } = s;
            return rest;
          }
          if (s.kind === 'user-review') {
            return {
              ...s,
              decisions: s.decisions.map(({ editDeltas: _ed, ...d }) => d),
            };
          }
          if (s.kind === 'code-generated') {
            const { fullCode: _fc, fullDiagnostics: _fd, promptSent: _ps, ...rest } = s;
            return { ...rest, codePreview: rest.codePreview.slice(0, 500) };
          }
          if (s.kind === 'user-committed') {
            const { fullCode: _fc, ...rest } = s;
            return { ...rest, codePreview: rest.codePreview.slice(0, 500) };
          }
          return s;
        }),
      }));

      localStorage.setItem(STORAGE_KEYS.INGESTION_JOURNALS, JSON.stringify(stripped));
    } catch {
      // Storage full — trim to 10 journals and retry
      try {
        this.journals = this.journals.slice(0, 10);
        localStorage.setItem(STORAGE_KEYS.INGESTION_JOURNALS, JSON.stringify(this.journals));
      } catch {
        // Give up
      }
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // Internal: Thumbnail + hash utilities for audit trail
  // ════════════════════════════════════════════════════════════════════════

  /** Generate a thumbnail from a base64 image, returning base64 JPEG + dimensions */
  private async _generateThumbnail(
    base64Data: string,
    mimeType: string,
    maxWidth = 300,
  ): Promise<{ thumbnail: string; width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, w, h);
        const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.7);
        const thumbnail = jpegDataUrl.split(',')[1];
        resolve({ thumbnail, width: img.width, height: img.height });
      };
      img.onerror = () => reject(new Error('Failed to load image for thumbnail'));
      img.src = `data:${mimeType};base64,${base64Data}`;
    });
  }

  /** Compute SHA-256 hash of a string */
  private async _hashData(data: string): Promise<string> {
    const encoder = new TextEncoder();
    const buffer = await crypto.subtle.digest('SHA-256', encoder.encode(data));
    return Array.from(new Uint8Array(buffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // ════════════════════════════════════════════════════════════════════════
  // Internal: Reactive update
  // ════════════════════════════════════════════════════════════════════════

  private _update(): void {
    this.generation++;
    this.host.requestUpdate();
  }
}
